<template>
  <section class="playback-diagnostics" aria-label="UAT 播放診斷">
    <header class="playback-diagnostics__header">
      <div>
        <strong>UAT 播放診斷</strong>
        <span class="playback-diagnostics__badge" :class="diagnosisClass">
          {{ diagnosisLabel }}
        </span>
      </div>
      <div class="playback-diagnostics__actions">
        <button type="button" class="btn btn-sm btn-outline-light" @click="copyDiagnostics">
          {{ copyLabel }}
        </button>
        <button type="button" class="btn btn-sm btn-outline-light" @click="$emit('clear')">
          清除紀錄
        </button>
        <button type="button" class="btn btn-sm btn-outline-light" @click="expanded = !expanded">
          {{ expanded ? '收合' : '展開' }}
        </button>
      </div>
    </header>

    <div v-if="expanded" class="playback-diagnostics__body">
      <p class="playback-diagnostics__notice">
        本面板只保留於目前頁面，不會上傳 Cookie、Policy、Signature 或 Key。
      </p>

      <div v-if="incident" class="playback-diagnostics__incident">
        <strong>最近異常：{{ eventLabel(incident.event.type) }}</strong>
        <span>{{ formatDate(incident.event.at) }}</span>
        <div>{{ incident.event.detail || '瀏覽器未提供額外訊息' }}</div>
      </div>

      <div class="playback-diagnostics__grid">
        <div v-for="row in liveRows" :key="row.label" class="playback-diagnostics__row">
          <span>{{ row.label }}</span>
          <code>{{ row.value }}</code>
        </div>
      </div>

      <div v-if="incident" class="playback-diagnostics__section">
        <h6>異常當下快照</h6>
        <div class="playback-diagnostics__grid">
          <div v-for="row in incidentRows" :key="row.label" class="playback-diagnostics__row">
            <span>{{ row.label }}</span>
            <code>{{ row.value }}</code>
          </div>
        </div>
      </div>

      <div class="playback-diagnostics__section">
        <h6>最近事件（最新在上）</h6>
        <div v-if="events.length" class="playback-diagnostics__events">
          <div v-for="event in events" :key="event.id" class="playback-diagnostics__event">
            <time>{{ formatTime(event.at) }}</time>
            <strong>{{ eventLabel(event.type) }}</strong>
            <span>{{ event.detail }}</span>
          </div>
        </div>
        <p v-else class="mb-0">尚無播放事件。</p>
      </div>

      <details class="playback-diagnostics__device">
        <summary>裝置資訊</summary>
        <code>{{ live && live.device ? live.device.userAgent : '尚未取得' }}</code>
      </details>
    </div>
  </section>
</template>

<script>
const EVENT_LABELS = {
  'diagnostics-enabled': '診斷啟用',
  'authorization-request': '取得影音授權',
  'authorization-success': '影音授權成功',
  'authorization-error': '影音授權失敗',
  'course-selected': '切換課程',
  'hls-init': 'HLS.js 初始化',
  'hls-manifest': 'HLS manifest 完成',
  'native-init': '原生 HLS 初始化',
  play: '要求播放',
  playing: '開始播放',
  pause: '暫停',
  waiting: '等待資料',
  stalled: '下載停滯',
  emptied: '媒體資源被清空',
  'media-error': '原生媒體錯誤',
  'hls-error': 'HLS 錯誤',
  'progress-stopped': '播放時間停止',
  'visibility-hidden': '頁面進入背景',
  'visibility-visible': '頁面回到前景',
  'recovery-start': '開始恢復',
  'recovery-success': '恢復成功',
  'recovery-failed': '恢復失敗',
  'player-rebuilt': '播放器已重建',
  ended: '播放結束',
  cleanup: '釋放舊播放器',
};

export default {
  name: 'PlaybackDiagnosticsPanel',
  props: {
    diagnostics: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      expanded: true,
      copyCompleted: false,
      copyFailed: false,
      copyResetTimer: null,
    };
  },
  computed: {
    live() {
      return this.diagnostics.live || null;
    },
    incident() {
      return this.diagnostics.incident || null;
    },
    events() {
      return this.diagnostics.events || [];
    },
    copyLabel() {
      if (this.copyFailed) return '複製失敗';
      return this.copyCompleted ? '已複製' : '複製診斷資訊';
    },
    diagnosisLabel() {
      const snapshot = this.incident ? this.incident.snapshot : this.live;
      const eventType = this.incident && this.incident.event
        ? this.incident.event.type
        : '';
      if (!snapshot) return '等待播放器';

      const authorization = snapshot.authorization || {};
      const hlsError = snapshot.lastHlsError || {};
      const media = snapshot.media || {};
      const buffered = media.buffered || {};
      const player = snapshot.player || {};

      if (
        authorization.remainingSeconds !== null
        && authorization.remainingSeconds <= 0
      ) return '影音授權可能到期';
      if (eventType === 'authorization-error') return '影音授權取得失敗';
      if (hlsError.responseCode === 401 || hlsError.responseCode === 403) {
        return '影音來源拒絕存取';
      }
      if (!this.incident) return '監測中';
      if (
        eventType === 'emptied'
        || player.mediaWasEmptied
        || (media.readyState === 0 && player.playbackIntended)
      ) {
        return '媒體資源被清空';
      }
      if (media.errorCode) return '瀏覽器媒體錯誤';
      if (
        eventType === 'progress-stopped'
        && Number.isFinite(buffered.aheadSeconds)
        && buffered.aheadSeconds >= 3
      ) return '播放器／解碼器可能卡住';
      if (
        (eventType === 'waiting' || eventType === 'stalled')
        && Number.isFinite(buffered.aheadSeconds)
        && buffered.aheadSeconds < 1.5
      ) return '影片資料下載不足';
      return this.incident ? '已捕捉異常' : '監測中';
    },
    diagnosisClass() {
      return this.incident ? 'is-warning' : 'is-healthy';
    },
    liveRows() {
      return this.snapshotRows(this.live);
    },
    incidentRows() {
      return this.snapshotRows(this.incident ? this.incident.snapshot : null);
    },
  },
  watch: {
    incident(value) {
      if (value) this.expanded = true;
    },
  },
  beforeDestroy() {
    if (this.copyResetTimer) clearTimeout(this.copyResetTimer);
  },
  methods: {
    eventLabel(type) {
      return EVENT_LABELS[type] || type || '未知事件';
    },
    formatDate(value) {
      if (!value) return '-';
      return new Date(value).toLocaleString('zh-TW', { hour12: false });
    },
    formatTime(value) {
      if (!value) return '-';
      return new Date(value).toLocaleTimeString('zh-TW', { hour12: false });
    },
    formatSeconds(value) {
      return Number.isFinite(value) ? `${value} 秒` : '-';
    },
    formatBoolean(value) {
      if (value === true) return '是';
      if (value === false) return '否';
      return '-';
    },
    formatConnection(connection) {
      if (!connection) return '-';
      const values = [connection.online ? 'online' : 'offline'];
      if (connection.effectiveType) values.push(connection.effectiveType);
      if (Number.isFinite(connection.downlinkMbps)) values.push(`${connection.downlinkMbps} Mbps`);
      if (Number.isFinite(connection.rttMs)) values.push(`RTT ${connection.rttMs}ms`);
      return values.join(' / ');
    },
    formatHlsError(error) {
      if (!error) return '-';
      return [
        error.type,
        error.details,
        error.event ? `event=${error.event}` : '',
        error.responseCode ? `HTTP ${error.responseCode}` : '',
        error.fatal ? 'fatal' : '',
        error.message || '',
      ].filter(Boolean).join(' / ') || '-';
    },
    snapshotRows(snapshot) {
      if (!snapshot) return [{ label: '狀態', value: '等待播放器資料' }];

      const media = snapshot.media || {};
      const buffered = media.buffered || {};
      const authorization = snapshot.authorization || {};
      const hls = snapshot.hls || {};
      const player = snapshot.player || {};
      const resolution = media.width && media.height
        ? `${media.width} × ${media.height}`
        : '-';
      const level = Number.isFinite(hls.activeHeight) ? `${hls.activeHeight}p` : '-';

      return [
        { label: '捕捉時間', value: this.formatDate(snapshot.capturedAt) },
        { label: '課程', value: `${snapshot.course.name || '-'} (${snapshot.course.code || '-'})` },
        { label: '播放模式', value: snapshot.playbackMode || '-' },
        { label: '來源', value: media.source || '-' },
        { label: 'readyState', value: `${media.readyState} / ${media.readyStateLabel || '-'}` },
        { label: 'networkState', value: `${media.networkState} / ${media.networkStateLabel || '-'}` },
        { label: '播放狀態', value: `paused=${this.formatBoolean(media.paused)}, intended=${this.formatBoolean(player.playbackIntended)}` },
        { label: '播放時間', value: `${this.formatSeconds(media.currentTime)} / ${this.formatSeconds(media.duration)}` },
        { label: 'Buffer ahead', value: this.formatSeconds(buffered.aheadSeconds) },
        { label: '停止時間', value: this.formatSeconds(player.stalledForSeconds) },
        { label: '解析度／HLS 畫質', value: `${resolution} / ${level}` },
        { label: 'MediaError', value: media.errorCode ? `${media.errorCode} / ${media.errorLabel} / ${media.errorMessage || '-'}` : '-' },
        { label: '最後 HLS 錯誤', value: this.formatHlsError(snapshot.lastHlsError) },
        { label: '授權狀態', value: authorization.status || '-' },
        { label: '授權取得時間', value: this.formatDate(authorization.refreshedAt) },
        { label: '授權到期時間', value: this.formatDate(authorization.expiresAt) },
        { label: '授權剩餘', value: this.formatSeconds(authorization.remainingSeconds) },
        { label: '網路', value: this.formatConnection(snapshot.connection) },
        { label: '頁面狀態', value: snapshot.document.visibilityState || '-' },
      ];
    },
    async copyDiagnostics() {
      const content = JSON.stringify({
        generatedAt: new Date().toISOString(),
        live: this.live,
        incident: this.incident,
        events: this.events,
      }, null, 2);

      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          try {
            await navigator.clipboard.writeText(content);
          } catch (error) {
            this.copyDiagnosticsFallback(content);
          }
        } else {
          this.copyDiagnosticsFallback(content);
        }
        this.copyCompleted = true;
        this.copyFailed = false;
        if (this.copyResetTimer) clearTimeout(this.copyResetTimer);
        this.copyResetTimer = setTimeout(() => {
          this.copyCompleted = false;
          this.copyFailed = false;
          this.copyResetTimer = null;
        }, 2000);
      } catch (error) {
        this.copyCompleted = false;
        this.copyFailed = true;
        if (this.copyResetTimer) clearTimeout(this.copyResetTimer);
        this.copyResetTimer = setTimeout(() => {
          this.copyFailed = false;
          this.copyResetTimer = null;
        }, 2000);
      }
    },
    copyDiagnosticsFallback(content) {
      const textArea = document.createElement('textarea');
      textArea.value = content;
      textArea.setAttribute('readonly', 'readonly');
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);

      try {
        textArea.select();
        textArea.setSelectionRange(0, textArea.value.length);
        if (!document.execCommand('copy')) throw new Error('copy command failed');
      } finally {
        document.body.removeChild(textArea);
      }
    },
  },
};
</script>

<style scoped>
.playback-diagnostics {
  width: 100%;
  margin: 1rem 0;
  border: 2px solid #22303f;
  border-radius: 6px;
  background: #101820;
  color: #eef5f8;
  font-size: 13px;
}

.playback-diagnostics__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: #22303f;
}

.playback-diagnostics__badge {
  display: inline-block;
  margin-left: 0.5rem;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 12px;
}

.playback-diagnostics__badge.is-healthy {
  background: #1f7a4d;
}

.playback-diagnostics__badge.is-warning {
  background: #c76b12;
}

.playback-diagnostics__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.playback-diagnostics__body {
  padding: 0.85rem 1rem 1rem;
}

.playback-diagnostics__notice {
  margin-bottom: 0.75rem;
  color: #b7c7cf;
}

.playback-diagnostics__incident {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.25rem 0.75rem;
  margin-bottom: 0.85rem;
  padding: 0.65rem 0.75rem;
  border-left: 4px solid #f0a341;
  background: #342819;
}

.playback-diagnostics__incident div {
  grid-column: 1 / -1;
}

.playback-diagnostics__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1px;
  background: #344653;
}

.playback-diagnostics__row {
  display: grid;
  grid-template-columns: minmax(110px, 35%) 1fr;
  gap: 0.5rem;
  min-width: 0;
  padding: 0.45rem 0.55rem;
  background: #17242d;
}

.playback-diagnostics__row span {
  color: #9fb3bf;
}

.playback-diagnostics code {
  overflow-wrap: anywhere;
  color: #e8f5fa;
  white-space: normal;
}

.playback-diagnostics__section {
  margin-top: 1rem;
}

.playback-diagnostics__section h6 {
  margin-bottom: 0.5rem;
  color: #fff;
}

.playback-diagnostics__events {
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid #344653;
}

.playback-diagnostics__event {
  display: grid;
  grid-template-columns: 80px 130px 1fr;
  gap: 0.5rem;
  padding: 0.4rem 0.55rem;
  border-bottom: 1px solid #2c3d48;
}

.playback-diagnostics__event:last-child {
  border-bottom: 0;
}

.playback-diagnostics__event time {
  color: #9fb3bf;
}

.playback-diagnostics__device {
  margin-top: 1rem;
}

.playback-diagnostics__device code {
  display: block;
  margin-top: 0.5rem;
}

@media (max-width: 767px) {
  .playback-diagnostics__header {
    align-items: flex-start;
    flex-direction: column;
  }

  .playback-diagnostics__grid {
    grid-template-columns: 1fr;
  }

  .playback-diagnostics__event {
    grid-template-columns: 70px 105px 1fr;
  }
}
</style>
